/**
 * Data-driven screen renderers: Level Select, Pre-run, Results, Practice.
 * Pure DOM construction from save + level data; callbacks wire back into the
 * game shell. The Results hierarchy follows spec Section 8 deliberately —
 * that screen sells the loop.
 */
import type { LevelFile, MedalTimes, ParsedLevel } from "../track/types";
import type { SaveStore, LevelEntry } from "../data/saveStore";
import { medalFor, nextMedalDelta, type Medal } from "../data/medals";
import { WORLD_NAMES } from "../levels";
import { formatTimeMs, gravityLabel, applyGravityClass } from "./hud";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
}

function medalDots(entry: LevelEntry | undefined, medals: MedalTimes | undefined): string {
  if (!medals) return "";
  const earned: Medal | null =
    entry?.bestTimeMs !== undefined ? medalFor(entry.bestTimeMs, medals) : null;
  const rank = { author: 4, gold: 3, silver: 2, bronze: 1 }[earned ?? "bronze"] ?? 0;
  const has = earned ? rank : 0;
  const dot = (cls: string, on: boolean, icon = "●"): string =>
    `<span class="medal-dot ${on ? cls : "medal-none"}">${icon}</span>`;
  return (
    dot("medal-bronze", has >= 1) +
    dot("medal-silver", has >= 2) +
    dot("medal-gold", has >= 3) +
    dot("medal-author", has >= 4, "★")
  );
}

function bitsStr(entry: LevelEntry | undefined): string {
  const n = entry ? entry.bits.filter(Boolean).length : 0;
  return `◆ ${n}/3`;
}

export function renderLevelList(
  levels: LevelFile[],
  save: SaveStore,
  onPick: (id: string) => void,
): void {
  const list = el("level-list");
  list.textContent = "";
  let lastWorld = -1;
  for (const level of levels) {
    if (level.world !== lastWorld) {
      lastWorld = level.world;
      const header = document.createElement("div");
      header.className = "world-header";
      header.textContent = `WORLD ${level.world} — ${WORLD_NAMES[level.world] ?? ""}`.toUpperCase();
      list.appendChild(header);
    }
    const entry = save.getLevel(level.id);
    const card = document.createElement("button");
    card.className = "level-card";
    card.innerHTML =
      `<span>${level.name}${entry?.deathless ? ' <span class="deathless-mark">✦</span>' : ""}</span>` +
      `<span class="meta">${bitsStr(entry)} · ` +
      `${entry?.bestTimeMs !== undefined ? formatTimeMs(entry.bestTimeMs) : "—"} ` +
      `${medalDots(entry, level.medalTimesMs)}</span>`;
    card.addEventListener("click", () => onPick(level.id));
    list.appendChild(card);
  }
}

export function renderPreRun(level: LevelFile, save: SaveStore): void {
  el("prerun-name").textContent = level.name;
  const medals = el("prerun-medals");
  if (level.medalTimesMs) {
    const m = level.medalTimesMs;
    medals.innerHTML =
      `<span class="medal-dot medal-bronze">●</span><span>${formatTimeMs(m.bronze)}</span>` +
      `<span class="medal-dot medal-silver">●</span><span>${formatTimeMs(m.silver)}</span>` +
      `<span class="medal-dot medal-gold">●</span><span>${formatTimeMs(m.gold)}</span>` +
      `<span class="medal-dot medal-author">★</span><span>${formatTimeMs(m.author)}</span>`;
  } else {
    medals.textContent = "";
  }
  const gravity = level.physics?.gravity ?? "normal";
  const gravEl = el("prerun-gravity");
  gravEl.textContent = gravityLabel(gravity, true);
  applyGravityClass(gravEl, gravity);
  const entry = save.getLevel(level.id);
  el("prerun-state").textContent = entry
    ? `Best ${entry.bestTimeMs !== undefined ? formatTimeMs(entry.bestTimeMs) : "—"} · ${bitsStr(entry)}` +
      (entry.deathless ? " · deathless ✦" : "")
    : "Not yet completed";
}

export interface ResultsData {
  finished: boolean;
  practice: boolean;
  timeMs: number;
  progressPct: number;
  bitsThisRun: number;
  levelName: string;
  medals?: MedalTimes;
  entry?: LevelEntry;
  isNewBest: boolean;
  deathlessEarned: boolean;
  hasNext: boolean;
  tutorial: boolean;
}

export function renderResults(data: ResultsData): void {
  const big = el("results-big");
  const medalLine = el("results-medal");
  const grid = el("results-grid");

  if (data.finished) {
    big.textContent = formatTimeMs(data.timeMs);
  } else {
    // Zeigarnik framing — this exact wording matters (spec 6.2)
    big.textContent = `${Math.floor(data.progressPct)}% — so close.`;
  }

  if (data.practice) {
    medalLine.textContent = "Practice — nothing written.";
  } else if (data.tutorial) {
    medalLine.textContent = "Medals: beat each level faster for bronze, silver, gold.";
  } else if (data.finished && data.medals) {
    const earned = medalFor(data.timeMs, data.medals);
    const next = nextMedalDelta(data.timeMs, data.medals);
    const name = earned ? earned[0]!.toUpperCase() + earned.slice(1) : "No medal";
    const chase = next
      ? ` ${next.medal[0]!.toUpperCase() + next.medal.slice(1)} is ${(next.deltaMs / 1000).toFixed(1)} s away.`
      : " That's the author time — nothing above it.";
    medalLine.textContent =
      `${name}.${chase}` +
      (data.isNewBest ? " New best." : "") +
      (data.deathlessEarned ? " Deathless ✦" : "");
  } else {
    medalLine.textContent = "";
  }

  if (data.entry && !data.practice && !data.tutorial) {
    grid.innerHTML =
      `<span>best ${data.entry.bestTimeMs !== undefined ? formatTimeMs(data.entry.bestTimeMs) : "—"}</span>` +
      `<span>◆ ${data.entry.bits.filter(Boolean).length}/3</span>` +
      `<span>${data.entry.deathless ? "deathless ✦" : "deathless —"}</span>`;
  } else {
    grid.innerHTML = data.finished ? `<span>◆ ${data.bitsThisRun} collected</span>` : "";
  }

  el("btn-results-next").style.display = data.hasNext && data.finished ? "" : "none";
  el("btn-results-retry").focus();
}

export function renderPracticeList(
  level: ParsedLevel,
  onPick: (startRow: number) => void,
): void {
  const list = el("practice-list");
  list.textContent = "";
  const sections = [0, ...level.checkpointRows];
  sections.forEach((row, i) => {
    const btn = document.createElement("button");
    btn.className = "practice-item";
    const pct = Math.floor((row / level.finishRow) * 100);
    btn.textContent = i === 0 ? "Start of level" : `Section ${i + 1} — ${pct}%`;
    btn.addEventListener("click", () => onPick(row));
    list.appendChild(btn);
  });
}
